import React from 'react';
import { motion } from 'framer-motion';
import { RocketIcon, ShieldCheckIcon, ClockIcon, FileTextIcon, ZapIcon } from 'lucide-react';
interface QuizCTABridgeProps {
  onRestart: () => void;
}
export function QuizCTABridge({
  onRestart
}: QuizCTABridgeProps) {
  return <motion.div className="min-h-screen w-full flex items-center justify-center px-4 py-12" initial={{
    opacity: 0,
    y: 30
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.5
  }} data-id="element-89">
      <div className="max-w-2xl w-full text-center" data-id="element-90">
        {/* Badge */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1,
        duration: 0.4
      }} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 mb-8" data-id="element-91">
          <RocketIcon className="w-4 h-4 text-amber-400" data-id="element-92" />
          <span className="text-sm text-amber-300 font-medium" data-id="element-93">
            Tu siguiente paso
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2,
        duration: 0.5
      }} className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight mb-6" data-id="element-94">
          Basado en tu perfil,{' '}
          <span className="text-transparent bg-clip-text" style={{
          backgroundImage: 'linear-gradient(135deg, #f59e0b, #ff6b2b)'
        }} data-id="element-95">
            tu plan de 48H ya está listo.
          </span>
        </motion.h1>

        <motion.p initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.35,
        duration: 0.4
      }} className="text-lg text-white/50 mb-10 max-w-lg mx-auto" data-id="element-96">
          Todo lo que necesitas para ejecutar tu ruta, sin improvisar.
        </motion.p>

        {/* Bullets */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.45,
        duration: 0.5
      }} className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-md p-6 sm:p-8 mb-10 text-left max-w-lg mx-auto" style={{
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)'
      }} data-id="element-97">
          <h3 className="text-sm font-bold uppercase tracking-wider text-white/40 mb-5" data-id="element-98">
            Dentro de 48H AI Cash Machine
          </h3>
          <div className="flex flex-col gap-4" data-id="element-99">
            <div className="flex items-start gap-3" data-id="element-100">
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mt-0.5" data-id="element-101">
                <ClockIcon className="w-4 h-4 text-amber-400" data-id="element-102" />
              </div>
              <p className="text-white/70 text-base" data-id="element-103">
                <strong className="text-white" data-id="element-104">
                  Qué hacer en las próximas 48 horas
                </strong>{' '}
                — sin improvisar, paso a paso
              </p>
            </div>
            <div className="flex items-start gap-3" data-id="element-105">
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mt-0.5" data-id="element-106">
                <ShieldCheckIcon className="w-4 h-4 text-amber-400" data-id="element-107" />
              </div>
              <p className="text-white/70 text-base" data-id="element-108">
                <strong className="text-white" data-id="element-109">Qué NO hacer</strong> para no
                perder tiempo ni dispersarte
              </p>
            </div>
            <div className="flex items-start gap-3" data-id="element-110">
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mt-0.5" data-id="element-111">
                <FileTextIcon className="w-4 h-4 text-amber-400" data-id="element-112" />
              </div>
              <p className="text-white/70 text-base" data-id="element-113">
                <strong className="text-white" data-id="element-114">
                  Scripts + plantillas + estructura
                </strong>{' '}
                — todo listo para copiar y ejecutar
              </p>
            </div>
          </div>
        </motion.div>

        {/* Main CTA */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.6,
        duration: 0.4,
        type: 'spring',
        stiffness: 200
      }} className="mb-6" data-id="element-115">
          <a href="https://48hrscashmachine.com/vsl" target="_blank" rel="noopener noreferrer" className="group relative inline-flex items-center gap-3 px-12 py-5 rounded-xl text-xl font-bold text-black transition-all duration-200 hover:scale-105 active:scale-100" style={{
          background: 'linear-gradient(135deg, #f59e0b, #ff6b2b)',
          boxShadow: '0 0 40px rgba(245, 158, 11, 0.35), 0 6px 24px rgba(255, 107, 43, 0.25)'
        }} aria-label="Ver mi Plan Completo de 48H" data-id="element-116">
            <ZapIcon className="w-6 h-6" data-id="element-117" />
            Ver mi Plan Completo de 48H
          </a>
        </motion.div>

        {/* Guarantee */}
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.8,
        duration: 0.4
      }} className="text-sm text-white/30 mb-8" data-id="element-118">
          Acceso inmediato. Implementación paso a paso.
        </motion.p>

        {/* Restart */}
        <motion.button initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1,
        duration: 0.4
      }} onClick={onRestart} className="text-xs text-white/20 hover:text-white/40 transition-colors underline underline-offset-2" data-id="element-119">
          Volver a hacer el test
        </motion.button>
      </div>
    </motion.div>;
}